// backend/utils/scoring_blank_guard.py의 resolve_item_blank_rule / resolve_item_floor_score /
// mentions_blank_zero와 같은 규칙이어야 모달에 보이는 점수와 실제 백지 점수가 맞는다.

const BLANK_RULE_KEYWORDS = ['백지', '미작성', '공백', '내용 없음', '내용없음', '무응답', '미제출'];
const FLOOR_CONDITION_KEYWORDS = ['백지', '미작성', '공백', '내용 없음'];
const ADDITIVE_TYPES = new Set([
  'additive_checklist',
  'hybrid_minimum_plus_checklist',
  'deduction_rubric',
  'bonus_rubric',
  'deduction',
  'bonus',
  'hybrid',
  'checklist',
]);

const BLANK_WORDS = '(?:백지|미작성|무응답|미제출|공란|빈\\s*칸|작성하지\\s*않|쓰지\\s*않|적지\\s*않'
  + '|답(?:을|안을)?\\s*안\\s*쓴|아무것도\\s*(?:쓰지|적지))';
// 구형 iOS Safari가 lookbehind를 못 읽으므로 앞 글자를 소비하는 방식으로 '10점'을 거른다.
const ZERO_POINTS = '(?:^|[^\\d.])0\\s*점';
const BLANK_ZERO_TEXT_RE = new RegExp(
  `${BLANK_WORDS}[^\\n]{0,20}?${ZERO_POINTS}|${ZERO_POINTS}[^\\n]{0,12}?${BLANK_WORDS}`
);

export const BLANK_POLICY_VALUES = ['auto', 'zero', 'floor'];

const toInt = (value) => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^-?\d+$/.test(text)) return parseInt(text, 10);
  const digits = text.replace(/[^\d-]/g, '');
  if (!digits || digits === '-') return null;
  const parsed = parseInt(digits, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const levelScore = (entry) => toInt(entry?.score ?? entry?.points);
const mentionsBlank = (text) => BLANK_RULE_KEYWORDS.some((keyword) => String(text || '').includes(keyword));
const pyRound = (value) => {
  const floor = Math.floor(value);
  const diff = value - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
};

export const mentionsBlankZero = (text) => BLANK_ZERO_TEXT_RE.test(String(text || ''));

export const hasItemBlankZeroRule = (item) => {
  if (!item || typeof item !== 'object') return false;
  const levels = Array.isArray(item.scoring_levels) ? item.scoring_levels : [];
  for (const level of levels) {
    if (!level || typeof level !== 'object' || levelScore(level) !== 0) continue;
    if (mentionsBlank(level.level || level.name)) return true;
    if (mentionsBlank(level.description || level.criteria)) return true;
  }
  const conditions = Array.isArray(item.minimum_conditions) ? item.minimum_conditions : [];
  if (conditions.some((cond) => (
    cond && typeof cond === 'object'
    && levelScore(cond) === 0
    && mentionsBlank(cond.condition || cond.level)
  ))) return true;
  const zeroConditions = Array.isArray(item.zero_score_conditions) ? item.zero_score_conditions : [item.zero_score_conditions];
  return zeroConditions.some((text) => typeof text === 'string' && mentionsBlank(text));
};

export const getItemFloorScore = (item) => {
  if (!item || typeof item !== 'object') return 0;
  const conditions = Array.isArray(item.minimum_conditions) ? item.minimum_conditions : [];
  for (const cond of conditions) {
    if (!cond || typeof cond !== 'object') continue;
    const label = String(cond.condition || cond.level || '');
    const score = levelScore(cond);
    if (FLOOR_CONDITION_KEYWORDS.some((keyword) => label.includes(keyword)) && score !== null && score > 0) {
      return score;
    }
  }
  const conditionScores = conditions
    .map((cond) => (cond && typeof cond === 'object' ? levelScore(cond) : null))
    .filter((score) => score !== null && score > 0);
  if (conditionScores.length) return Math.min(...conditionScores);

  const maxPoints = toInt(item.max_points || item.points) || 0;
  const scoringType = String(item.scoring_type || '').trim().toLowerCase();
  if (ADDITIVE_TYPES.has(scoringType)) return Math.max(pyRound(maxPoints * 0.25), 0);

  const levelScores = (Array.isArray(item.scoring_levels) ? item.scoring_levels : [])
    .map((level) => (level && typeof level === 'object' ? levelScore(level) : null))
    .filter((score) => score !== null && score > 0);
  if (levelScores.length) return Math.min(...levelScores);
  return Math.max(pyRound(maxPoints * 0.25), 0);
};

const collectItems = (rubrics) => rubrics
  .filter(Boolean)
  .flatMap((rubric) => (Array.isArray(rubric?.evaluation_items) ? rubric.evaluation_items : []))
  .filter((item) => item && typeof item === 'object');

export const getBlankPolicyDefault = ({ rubrics = [], rubricText = '', customInstruction = '' } = {}) => {
  const items = collectItems(rubrics);
  const floorTotal = items.reduce((sum, item) => sum + getItemFloorScore(item), 0);
  let reason = null;
  if (items.some(hasItemBlankZeroRule) || mentionsBlankZero(rubricText)) reason = 'rubric';
  else if (mentionsBlankZero(customInstruction)) reason = 'instruction';
  return {
    policy: reason ? 'zero' : 'floor',
    reason,
    floorTotal,
    hasItems: items.length > 0,
  };
};

// 선생님이 직접 고르지 않았고 기본이 최저점이면 'auto'로 보내 서버가 지시문의 '백지 0점'도 반영하게 한다.
export const resolveBlankPolicyForRequest = (choice, defaults) => {
  if (choice === 'zero' || choice === 'floor') return choice;
  return defaults?.policy === 'zero' ? 'zero' : 'auto';
};
